/* eslint-disable unicorn/filename-case -- kebab-case per docs/conventions/core.md:
   this file exports functions, not a React component. */
import {
  businessWallClockAsUtc,
  calendarDateOfUtcInstant,
  isCalendarDate,
  parseInstantOrNull,
  toPrismaDate,
  type CalendarDate,
} from '@nudool/shared';

import type { ExportType } from './TableFeatures.js';

/** What a cell becomes in an export: CSV writes `text`, XLSX writes `value` typed by `kind`. */
export type FormattedExportValue =
  | { kind: 'empty'; text: '' }
  | { kind: 'text'; text: string }
  | { kind: 'number'; text: string; value: number; format: string }
  | { kind: 'date'; text: string; value: Date; format: string };

const EMPTY: FormattedExportValue = { kind: 'empty', text: '' };

// Thousands separators throughout (decision 14).
const INTEGER_FORMATTER = new Intl.NumberFormat('en-US', {
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});
// Up to 9 places so a 0.0004 g quantity survives, while float noise past that is still dropped.
const DECIMAL_FORMATTER = new Intl.NumberFormat('en-US', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 9,
});
const CURRENCY_FORMATTER = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
});

function pad(value: number, length = 2): string {
  return String(value).padStart(length, '0');
}

function formatUtcTimePart(date: Date): string {
  return `${pad(date.getUTCHours())}:${pad(date.getUTCMinutes())}:${pad(date.getUTCSeconds())}`;
}

// GraphQL sends instants as ISO strings with an offset; anything else is not read as an instant.
function toInstant(value: unknown): Date | null {
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  if (typeof value === 'string') return parseInstantOrNull(value);
  if (typeof value !== 'number') return null;
  // eslint-disable-next-line dates/no-date-parsing -- epoch milliseconds: the typeof guard above
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

// A date-only value is written as `YYYY-MM-DD` or stored at UTC midnight, so it keeps its UTC day.
function toCalendarDay(value: unknown): CalendarDate | null {
  if (isCalendarDate(value)) return value;
  const instant = toInstant(value);
  return instant ? calendarDateOfUtcInstant(instant) : null;
}

function toNumber(value: unknown): number | null {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

/** Cannot tell a price from a count, so money and percent columns must declare exportType. */
export function inferExportType(value: unknown): ExportType | 'array' | 'object' | 'empty' {
  if (value === null || value === undefined) return 'empty';
  if (typeof value === 'boolean') return 'boolean';
  if (value instanceof Date) return 'datetime';
  if (Array.isArray(value)) return 'array';
  if (typeof value === 'number') return Number.isInteger(value) ? 'integer' : 'decimal';
  if (typeof value === 'object') return 'object';
  return 'text';
}

/** A value its declared type can't satisfy falls back to text, losing format, never data. */
export function formatExportValue(value: unknown, exportType?: ExportType): FormattedExportValue {
  if (value === null || value === undefined) return EMPTY;

  const resolved = exportType ?? inferExportType(value);

  switch (resolved) {
    case 'boolean': {
      return { kind: 'text', text: value ? 'True' : 'False' };
    }
    case 'date': {
      const day = toCalendarDay(value);
      if (!day) break;
      return { kind: 'date', text: day, value: toPrismaDate(day), format: 'yyyy-mm-dd' };
    }
    case 'datetime': {
      const instant = toInstant(value);
      if (!instant) break;
      // Pacific, as the grid shows it; the cell's UTC fields carry that wall clock to the XLSX writer.
      const wallClock = businessWallClockAsUtc(instant);
      const text = `${calendarDateOfUtcInstant(wallClock)} ${formatUtcTimePart(wallClock)}`;
      return { kind: 'date', text, value: wallClock, format: 'yyyy-mm-dd hh:mm:ss' };
    }
    case 'currency': {
      const number = toNumber(value);
      if (number === null) break;
      return {
        kind: 'number',
        text: CURRENCY_FORMATTER.format(number),
        value: number,
        format: '"$"#,##0.00',
      };
    }
    case 'percent': {
      const number = toNumber(value);
      if (number === null) break;
      // Percent units (Decimal(5,2)); the XLSX writer divides by 100 once for Excel's % format.
      return {
        kind: 'number',
        text: `${DECIMAL_FORMATTER.format(number)}%`,
        value: number,
        format: '#,##0.00%',
      };
    }
    case 'integer': {
      const number = toNumber(value);
      if (number === null) break;
      return {
        kind: 'number',
        text: INTEGER_FORMATTER.format(number),
        value: number,
        format: '#,##0',
      };
    }
    case 'decimal': {
      const number = toNumber(value);
      if (number === null) break;
      return {
        kind: 'number',
        text: DECIMAL_FORMATTER.format(number),
        value: number,
        format: '#,##0.00#######',
      };
    }
    case 'array': {
      return { kind: 'text', text: (value as unknown[]).join(',') };
    }
    case 'object': {
      // JSON.stringify throws on a bigint anywhere in the object (e.g. a Prisma numeric id) rather than silently omitting it, unlike undefined/function/symbol.
      return {
        kind: 'text',
        text: JSON.stringify(value, (_key, entry) =>
          typeof entry === 'bigint' ? entry.toString() : entry,
        ),
      };
    }
    default: {
      break;
    }
  }

  return { kind: 'text', text: String(value) };
}
